import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'antd';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DotsSixVertical, Eye, EyeSlash } from '@phosphor-icons/react';
import Button from '../../components/Button';
import { captureAnalytics } from '../../actions/preload';
import { navPreferenceKey, orderNavButtons, slotInMissingKeys } from './navPreferences';
import './customizeNavModal.scss';

const SortableNavRow = ({
  page, hidden, toggleDisabled, onToggleHidden,
}) => {
  const id = navPreferenceKey(page);
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`customize-nav-row ${hidden ? 'customize-nav-row--hidden' : ''} ${isDragging ? 'customize-nav-row--dragging' : ''}`}
      data-testid={`customize-nav-row-${id}`}
    >
      <button
        type="button"
        className="customize-nav-row__handle"
        aria-label={`Drag to reorder ${page.label}`}
        // dnd-kit requires its passthrough attributes and listeners on the drag handle.
        /* eslint-disable-next-line react/jsx-props-no-spreading */
        {...attributes}
        /* eslint-disable-next-line react/jsx-props-no-spreading */
        {...listeners}
      >
        <DotsSixVertical size={18} weight="bold" />
      </button>
      <span className="customize-nav-row__icon">{page.icon}</span>
      <span className="customize-nav-row__label">{page.label}</span>
      <button
        type="button"
        className="customize-nav-row__toggle"
        aria-label={hidden ? `Show ${page.label}` : `Hide ${page.label}`}
        aria-pressed={hidden}
        disabled={toggleDisabled}
        title={toggleDisabled ? 'At least one page must stay visible' : undefined}
        onClick={() => onToggleHidden(id)}
      >
        {hidden ? <EyeSlash size={18} /> : <Eye size={18} />}
      </button>
    </li>
  );
};

SortableNavRow.propTypes = {
  page: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.node.isRequired,
  }).isRequired,
  hidden: PropTypes.bool.isRequired,
  toggleDisabled: PropTypes.bool.isRequired,
  onToggleHidden: PropTypes.func.isRequired,
};

const CustomizeNavModal = ({
  visible, pages, preferences, onCancel, onSave,
}) => {
  // Only the user's edits live in state. Everything else is derived from the
  // saved preferences and the pages the user has right now, because access
  // checks can settle after the editor opens. NewNav remounts this component
  // for each opening (keyed on the editor session), which resets the drafts.
  const [draftOrder, setDraftOrder] = useState(null); // null = not reordered
  const [hiddenEdits, setHiddenEdits] = useState({}); // order key -> hidden, per toggle
  const [resetRequested, setResetRequested] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragging, setDragging] = useState(false);
  // Pages the user dragged, for analytics only, so a ref (no re-render).
  const movedKeys = useRef(new Set());

  const base = resetRequested ? null : preferences;
  const pageKeys = pages.map(navPreferenceKey);
  const pagesByKey = new Map(pages.map((page) => [navPreferenceKey(page), page]));
  const savedOrder = orderNavButtons(pages, base).map(navPreferenceKey);
  // A page whose access check settles after the user reordered is missing from
  // the draft; it slots in after the page it follows in the saved order.
  const order = slotInMissingKeys((draftOrder || savedOrder).filter((key) => pagesByKey.has(key)), savedOrder);
  const buttonKey = (key) => pagesByKey.get(key).key;
  const savedHidden = new Set(base?.hidden || []);
  // The nav shows every page when saved choices would hide them all, so the
  // editor displays the same.
  const savedHidesAll = order.every((key) => savedHidden.has(buttonKey(key)));
  const isHidden = (key) => hiddenEdits[key] ?? (!savedHidesAll && savedHidden.has(buttonKey(key)));
  // What a save keeps hidden. Untouched, that show-everything view is display
  // only: a page that loads later can make the saved choices apply again, so
  // they are kept. Once the user toggles something, they are working from
  // what the editor shows, so that is saved.
  const hasHiddenEdits = Object.keys(hiddenEdits).length > 0;
  const willHide = (key) => (savedHidesAll && hasHiddenEdits
    ? isHidden(key)
    : hiddenEdits[key] ?? savedHidden.has(buttonKey(key)));
  const hiddenKeys = new Set(order.filter(isHidden));
  const visibleCount = order.length - hiddenKeys.size;
  const orderIsDefault = order.join() === pageKeys.join();
  const isDefault = hiddenKeys.size === 0 && orderIsDefault;

  // Saved keys for pages not shown right now (access still loading, or gone)
  // keep their place unless the user resets.
  const savedOrderKeys = base?.order || [];
  const unshownOrderKeys = savedOrderKeys.filter((key) => !pagesByKey.has(key));
  const buttonKeys = new Set(pages.map((page) => page.key));
  const unshownHiddenKeys = (base?.hidden || []).filter((key) => !buttonKeys.has(key));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = ({ active, over }) => {
    setDragging(false);
    if (!over || active.id === over.id) return;
    const toIndex = order.indexOf(over.id);
    setDraftOrder(arrayMove(order, order.indexOf(active.id), toIndex));
    movedKeys.current.add(active.id);
    captureAnalytics('nav_page_moved', { page_key: active.id, to_index: toIndex });
  };

  const toggleHidden = (key) => {
    const nextHidden = !isHidden(key);
    captureAnalytics('nav_page_visibility_toggled', { page_key: key, hidden: nextHidden });
    setHiddenEdits((current) => ({ ...current, [key]: nextHidden }));
  };

  const preferencesToSave = () => {
    const hidden = [];
    order.forEach((key) => { if (willHide(key)) hidden.push(buttonKey(key)); });
    return {
      // A default order saves as empty, so pages added later land in their
      // designed position instead of the end.
      order: orderIsDefault && unshownOrderKeys.length === 0 ? [] : slotInMissingKeys(order, savedOrderKeys),
      hidden: [...hidden, ...unshownHiddenKeys],
    };
  };

  const handleSave = async () => {
    setSaving(true);
    let result;
    try {
      result = await onSave(preferencesToSave(), {
        moved: movedKeys.current.size,
        hidden_count: order.filter(willHide).length,
        reset: resetRequested,
      });
    } finally {
      setSaving(false);
    }
    if (!result?.ok) {
      /* global toastr */
      toastr.error(result?.message || 'Could not save your navigation. Please try again.');
    }
  };

  // Closing mid-save would let a reopened editor be closed (and its drafts
  // dropped) when this save finishes, and Escape mid-drag only cancels the
  // drag, so every close path waits for both.
  const canClose = !saving && !dragging;
  const handleCancel = () => {
    if (!canClose) return;
    captureAnalytics('nav_customizer_cancelled');
    onCancel();
  };

  const resetToDefault = () => {
    captureAnalytics('nav_customizer_reset_clicked');
    setResetRequested(true);
    setDraftOrder(null);
    setHiddenEdits({});
    movedKeys.current = new Set();
  };

  return (
    <Modal
      visible={visible}
      title="Customize navigation"
      onCancel={handleCancel}
      closable={canClose}
      maskClosable={canClose}
      keyboard={canClose}
      width={420}
      destroyOnClose
      wrapClassName="customize-nav-modal"
      footer={(
        <div className="customize-nav-modal__footer">
          <Button
            type="tertiary"
            size="small"
            label="Reset to default"
            onClick={resetToDefault}
            // Saved choices for pages not shown here still count as customized.
            disabled={saving || (isDefault && !base)}
          />
          <div className="customize-nav-modal__actions">
            <Button type="secondary" size="small" label="Cancel" onClick={handleCancel} disabled={saving} />
            <Button
              type="primary"
              size="small"
              label={saving ? 'Saving…' : 'Save'}
              onClick={handleSave}
              disabled={saving || visibleCount === 0}
            />
          </div>
        </div>
      )}
    >
      <p className="customize-nav-modal__hint">
        Drag pages to reorder them. Hidden pages are still reachable from their links.
      </p>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={() => setDragging(true)}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setDragging(false)}
      >
        <SortableContext items={order} strategy={verticalListSortingStrategy}>
          <ul className="customize-nav-list">
            {order.map((key) => (
              <SortableNavRow
                key={key}
                page={pagesByKey.get(key)}
                hidden={hiddenKeys.has(key)}
                toggleDisabled={!hiddenKeys.has(key) && visibleCount <= 1}
                onToggleHidden={toggleHidden}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>
    </Modal>
  );
};

CustomizeNavModal.defaultProps = {
  preferences: null,
};

CustomizeNavModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  pages: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.node.isRequired,
  })).isRequired,
  preferences: PropTypes.shape({
    order: PropTypes.arrayOf(PropTypes.string),
    hidden: PropTypes.arrayOf(PropTypes.string),
  }),
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default CustomizeNavModal;
